import React from "react";

import { useMutation, useSubscription } from "@apollo/client";
import { Typography } from "@mui/material";
import { makeStyles } from "tss-react/mui";

import NoMealsIcon from "@/components/Icons/NoMealsIcon";
import { graphql } from "@/gql";
import { MealKind } from "@/gql/graphql";
import useFragment from "@/hooks/useFragment";
import { humanize } from "@/utils/string";

import MealFoodItem from "./MealFoodItem";
import MealMacros from "./MealMacros";
import MealPhotos from "./MealPhotos";
import MealRatingButton from "./MealRatingButton";
import MealTypeIcon from "./MealTypeIcon";

interface Props {
  id?: string;
  mealType: MealKind;
}

const mealDetailsFragment = graphql(`
  fragment MealDetails on Meal {
    id
    gid
    mealType
    description
    skipped
    energyKcal
    photos {
      ...MealPhoto
    }
    mealFoods {
      ...MealFoodItem
    }
    rating
    coachRating
  }
`);

const updateMealCoachRatingMutation = graphql(`
  mutation UpdateMealCoachRating($input: UpdateMealInput!) {
    updateMeal(input: $input) {
      meal {
        id
        coachRating
      }
    }
  }
`);

const mealUpdatedSubscription = graphql(`
  subscription MealUpdated($gid: ID!) {
    synchronize(model: "Meal", action: UPDATE, gid: $gid) {
      node {
        ... on Meal {
          ...MealDetails
        }
      }
    }
  }
`);

const DUMMY_DESCRIPTION = "initial meal";

const MealDetails: React.FC<Props> = ({ id, mealType }) => {
  const { classes, cx } = useStyles();

  const [meal] = useFragment(mealDetailsFragment, `Meal:${id}`);
  const skipped = !!meal?.skipped;
  const mealFoodsShown = !skipped && (meal?.mealFoods.length ?? 0) > 0;
  const mealDescriptionShown =
    !skipped && !!meal?.description && meal.description != DUMMY_DESCRIPTION;
  const hasPhoto = (meal?.photos.length ?? 0) > 0;
  const mealPhotosShown =
    !skipped && (mealFoodsShown || mealDescriptionShown || hasPhoto);
  const ratingsShown = mealPhotosShown;

  const [updateMealCoachRating] = useMutation(updateMealCoachRatingMutation);
  const updateCoachRating = (coachRating: number) => async () => {
    await updateMealCoachRating({
      variables: { input: { mealId: id ?? "", patch: { coachRating } } },
      optimisticResponse: {
        updateMeal: {
          meal: {
            __typename: "Meal",
            id: id ?? "",
            coachRating,
          },
        },
      },
    });
  };

  useSubscription(mealUpdatedSubscription, {
    variables: { gid: meal?.gid ?? "" },
  });

  return (
    <section id={mealType} className={classes.root}>
      <div className={classes.header}>
        <MealTypeIcon type={mealType} grayscale={!!meal?.skipped} />
        <div>
          <Typography className={classes.title}>
            {humanize(mealType)} details
          </Typography>
          {meal?.skipped ? (
            <Typography className={cx(classes.subtitle, classes.skipped)}>
              Skipped
            </Typography>
          ) : (
            <Typography className={classes.subtitle}>
              Calorie consumed: {meal?.energyKcal.toFixed() ?? 0}
            </Typography>
          )}
        </div>
      </div>
      <div className={classes.content}>
        {mealPhotosShown && <MealPhotos data={meal?.photos ?? []} />}
        <div className={classes.foods}>
          {mealDescriptionShown && (
            <Typography className={classes.description}>
              {meal?.description}
            </Typography>
          )}
          {mealFoodsShown &&
            meal?.mealFoods.map((mealFood) => (
              <MealFoodItem key={mealFood.id} id={mealFood.id} />
            ))}
          {!mealFoodsShown && !mealDescriptionShown && (
            <div className={classes.foodsEmpty}>
              <NoMealsIcon />
              <Typography className={classes.foodsEmptyText}>
                No food item added yet
              </Typography>
            </div>
          )}
        </div>
        {mealFoodsShown && <MealMacros meal={meal} />}
      </div>
      {ratingsShown && (
        <div className={classes.ratings}>
          <div className={classes.ratingContainer}>
            <Typography className={classes.ratingLabel}>
              Client rate:
            </Typography>
            <MealRatingButton rating={meal?.rating} disabled />
          </div>
          <div className={classes.ratingContainer}>
            <Typography className={classes.ratingLabel}>Rate Meal:</Typography>
            <MealRatingButton
              rating={5}
              selected={meal?.coachRating === 5}
              onClick={updateCoachRating(5)}
            />
            <MealRatingButton
              rating={4}
              selected={meal?.coachRating === 4}
              onClick={updateCoachRating(4)}
            />
            <MealRatingButton
              rating={3}
              selected={meal?.coachRating === 3}
              onClick={updateCoachRating(3)}
            />
            <MealRatingButton
              rating={2}
              selected={meal?.coachRating === 2}
              onClick={updateCoachRating(2)}
            />
            <MealRatingButton
              rating={1}
              selected={meal?.coachRating === 1}
              onClick={updateCoachRating(1)}
            />
          </div>
        </div>
      )}
    </section>
  );
};

const useStyles = makeStyles()({
  root: {
    height: "100%",
    display: "flex",
    flexDirection: "column",
    padding: 24,
    minHeight: 300,
    backgroundColor: "#fff",
    gap: 24,
  },
  header: {
    display: "flex",
    alignItems: "center",
    gap: 18,
  },
  title: {
    color: "#363E45",
    fontSize: 20,
    fontWeight: "bold",
  },
  subtitle: {
    color: "#4C515A",
    fontSize: 14,
    fontWeight: 600,
  },
  skipped: {
    color: "#F27373",
  },
  content: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
  },
  description: {
    marginTop: 8,
    color: "#4C515A",
    fontSize: 14,
    fontWeight: 600,
  },
  macros: {
    flex: 1,
    display: "flex",
    justifyContent: "space-around",
  },
  foods: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    gap: 8,
    marginTop: 8,
  },
  foodsEmpty: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifySelf: "center",
    alignSelf: "center",
    margin: "auto 0",
  },
  foodsEmptyText: {
    marginTop: 12,
    color: "#AAB0BB",
    fontSize: 14,
    fontWeight: 600,
  },
  ratings: {
    display: "flex",
    justifyContent: "space-between",
    gap: 8,
    marginTop: "auto",
    paddingTop: 8,
    borderTop: "3px solid #EDF0F5",
  },
  ratingContainer: {
    display: "flex",
    alignItems: "center",
    gap: 8,
  },
  ratingLabel: {
    color: "#4C515A",
    fontSize: 14,
    fontWeight: 600,
  },
  rating: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    flexShrink: 0,
    width: 36,
    height: 36,
    border: "1px solid #EEE",
    borderRadius: 9999,
  },
  ratingIcon: {
    maxWidth: 24,
    maxHeight: 24,
  },
});

export default MealDetails;
